/**
 * Opportunity Dataset Review - Global Master Dataset View
 * Displays 60 opportunities organized by SEU Type as an editable table
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Database, Download, Upload, ChevronDown, ChevronRight, Search, RefreshCw, Plus,
  Edit2, Save, X, Trash2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// SEU Type colors for visual distinction
const SEU_COLORS = {
  'A. ELECTRICAL — MOTORS, DRIVES, DISTRIBUTION': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', badge: 'bg-blue-600' },
  'B. THERMAL / FUEL': { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800', badge: 'bg-orange-600' },
  'C. COOLING & HVAC': { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-800', badge: 'bg-cyan-600' },
  'D. WATER & EFFLUENT': { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-800', badge: 'bg-teal-600' },
  'E. WASTE': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', badge: 'bg-amber-600' },
  'F. PROCESS & DIGITAL': { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800', badge: 'bg-purple-600' },
};

const OpportunityDatasetReview = ({ study, masterDataset, onRefresh }) => {
  // Route API calls through site-level endpoints when study has a site_id
  const masterBaseUrl = study?.site_id
    ? `${API}/api/opportunity-v2/sites/${study.site_id}/master-dataset`
    : `${API}/api/opportunity-v2/master-dataset`;
  const [expandedSEUs, setExpandedSEUs] = useState([]);
  const [expandedOpps, setExpandedOpps] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState({
    seu_type: '',
    opportunity_name: '',
    capex_item_name: '',
    technical_spec: '',
    capacity_basis: 'kW',
    unit_cost_low: 0,
    unit_cost_high: 0,
    currency: 'INR',
    typical_makes: '',
    lifecycle_years: 10
  });

  useEffect(() => {
    // Auto-expand all SEU types on load
    if (masterDataset?.seu_types) {
      setExpandedSEUs(masterDataset.seu_types);
    }
  }, [masterDataset]);

  const handleSaveEdit = async (item) => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.put(
        `${masterBaseUrl}/${item.capex_item_id}`,
        {
          study_capex_id: item.capex_item_id,
          ...editData
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingRow(null);
      setEditData({});
      onRefresh();
    } catch (error) {
      alert('Error saving: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.seu_type || !newItem.opportunity_name || !newItem.capex_item_name) {
      alert('Please fill SEU Type, Opportunity Name, and CAPEX Item Name');
      return;
    }
    
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${masterBaseUrl}`,
        {
          ...newItem,
          typical_makes: newItem.typical_makes.split(',').map(s => s.trim()).filter(Boolean)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowAddModal(false);
      setNewItem({
        seu_type: '',
        opportunity_name: '',
        capex_item_name: '',
        technical_spec: '',
        capacity_basis: 'kW',
        unit_cost_low: 0,
        unit_cost_high: 0,
        currency: 'INR',
        typical_makes: '',
        lifecycle_years: 10
      });
      onRefresh();
    } catch (error) {
      alert('Error adding: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (capexItemId) => {
    if (!window.confirm('Delete this CAPEX item?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${masterBaseUrl}/${capexItemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh();
    } catch (error) {
      alert('Error deleting: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDownloadCSV = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${masterBaseUrl}/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'opportunity_master_dataset.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error downloading: ' + error.message);
    }
  };

  const handleUploadCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(
        `${masterBaseUrl}/import`,
        formData,
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      alert(`Success! ${res.data.inserted_count || 0} items imported, ${res.data.updated_count || 0} items updated.`);
      onRefresh();
    } catch (error) {
      alert('Error uploading: ' + (error.response?.data?.detail || error.message));
    } finally {
      event.target.value = '';
    }
  };

  const toggleSEU = (seuType) => {
    if (expandedSEUs.includes(seuType)) {
      setExpandedSEUs(expandedSEUs.filter(s => s !== seuType));
    } else {
      setExpandedSEUs([...expandedSEUs, seuType]);
    }
  };

  const toggleOpp = (oppName) => {
    if (expandedOpps.includes(oppName)) {
      setExpandedOpps(expandedOpps.filter(o => o !== oppName));
    } else {
      setExpandedOpps([...expandedOpps, oppName]);
    }
  };

  if (!masterDataset) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const groupedBySeu = masterDataset.grouped_by_seu || {};

  // Filter by search
  const filterItems = (items) => {
    if (!searchTerm) return items;
    return items.filter(item => 
      item.capex_item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.technical_spec?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.typical_makes?.some(m => m.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white rounded-lg border border-gray-200 p-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Search */}
          <div className="flex-1 max-w-md flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search CAPEX items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 outline-none text-sm"
              data-testid="search-capex"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAddModal(true)}
              className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 text-sm"
              data-testid="add-row-btn"
            >
              <Plus className="w-4 h-4" />
              Add Row
            </button>
            
            <button
              onClick={() => setExpandedSEUs(expandedSEUs.length === Object.keys(groupedBySeu).length ? [] : Object.keys(groupedBySeu))}
              className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              {expandedSEUs.length === Object.keys(groupedBySeu).length ? 'Collapse All' : 'Expand All'}
            </button>
            
            <button
              onClick={handleDownloadCSV}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>

            <label className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm cursor-pointer">
              <Upload className="w-4 h-4" />
              Import CSV
              <input type="file" accept=".csv" onChange={handleUploadCSV} className="hidden" />
            </label>

            <button
              onClick={onRefresh}
              className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Dataset Table by SEU Type */}
      <div className="space-y-3">
        {Object.entries(groupedBySeu).map(([seuType, seuData]) => {
          const colors = SEU_COLORS[seuType] || { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800', badge: 'bg-gray-600' };
          const isExpanded = expandedSEUs.includes(seuType);
          
          return (
            <div key={seuType} className={`rounded-lg border ${colors.border} overflow-hidden`}>
              {/* SEU Type Header */}
              <button
                onClick={() => toggleSEU(seuType)}
                className={`w-full flex items-center justify-between px-4 py-3 ${colors.bg} hover:opacity-90 transition`}
                data-testid={`seu-${seuType.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  <span className={`font-bold ${colors.text}`}>{seuType}</span>
                  <span className={`px-2 py-0.5 ${colors.badge} text-white text-xs rounded-full`}>
                    {seuData.count} items
                  </span>
                </div>
              </button>

              {/* Opportunities under this SEU */}
              {isExpanded && (
                <div className="divide-y divide-gray-100">
                  {Object.entries(seuData.opportunities).map(([oppName, items]) => {
                    const filteredItems = filterItems(items);
                    if (searchTerm && filteredItems.length === 0) return null;
                    
                    const isOppExpanded = expandedOpps.includes(oppName) || !searchTerm;
                    
                    return (
                      <div key={oppName}>
                        {/* Opportunity Header */}
                        <button
                          onClick={() => toggleOpp(oppName)}
                          className="w-full flex items-center gap-2 px-6 py-2 bg-white hover:bg-gray-50 text-left"
                        >
                          {isOppExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                          <span className="font-medium text-gray-700">{oppName}</span>
                          <span className="text-xs text-gray-500">({filteredItems.length} items)</span>
                        </button>

                        {/* CAPEX Items Table */}
                        {isOppExpanded && (
                          <div className="overflow-x-auto bg-white">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 border-y border-gray-200">
                                <tr>
                                  <th className="px-3 py-2 text-left font-semibold text-gray-600">CAPEX Item</th>
                                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Technical Spec</th>
                                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Basis</th>
                                  <th className="px-3 py-2 text-right font-semibold text-gray-600">Cost Low</th>
                                  <th className="px-3 py-2 text-right font-semibold text-gray-600">Cost High</th>
                                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Currency</th>
                                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Makes/Models</th>
                                  <th className="px-3 py-2 text-center font-semibold text-gray-600">Life (yr)</th>
                                  <th className="px-3 py-2 text-center font-semibold text-gray-600">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {filteredItems.map((item) => {
                                  const isEditing = editingRow === item.capex_item_id;
                                  
                                  return (
                                    <tr key={item.capex_item_id} className="hover:bg-gray-50">
                                      <td className="px-3 py-2 font-medium text-gray-900">
                                        {item.capex_item_name}
                                      </td>
                                      <td className="px-3 py-2 text-gray-700">
                                        {isEditing ? (
                                          <input
                                            type="text"
                                            defaultValue={item.technical_spec}
                                            onChange={(e) => setEditData({...editData, technical_spec: e.target.value})}
                                            className="w-full px-2 py-1 border border-indigo-400 rounded text-sm"
                                          />
                                        ) : item.technical_spec}
                                      </td>
                                      <td className="px-3 py-2 text-gray-600">{item.capacity_basis}</td>
                                      <td className="px-3 py-2 text-right text-gray-900">
                                        {isEditing ? (
                                          <input
                                            type="number"
                                            defaultValue={item.unit_cost_low}
                                            onChange={(e) => setEditData({...editData, unit_cost_low: parseFloat(e.target.value)})}
                                            className="w-24 px-2 py-1 border border-indigo-400 rounded text-sm text-right"
                                          />
                                        ) : item.unit_cost_low?.toLocaleString()}
                                      </td>
                                      <td className="px-3 py-2 text-right text-gray-900">
                                        {isEditing ? (
                                          <input
                                            type="number"
                                            defaultValue={item.unit_cost_high}
                                            onChange={(e) => setEditData({...editData, unit_cost_high: parseFloat(e.target.value)})}
                                            className="w-24 px-2 py-1 border border-indigo-400 rounded text-sm text-right"
                                          />
                                        ) : item.unit_cost_high?.toLocaleString()}
                                      </td>
                                      <td className="px-3 py-2 text-gray-600">{item.currency}</td>
                                      <td className="px-3 py-2 text-gray-600 text-xs">
                                        {Array.isArray(item.typical_makes) ? item.typical_makes.join(', ') : item.typical_makes}
                                      </td>
                                      <td className="px-3 py-2 text-center text-gray-600">
                                        {isEditing ? (
                                          <input
                                            type="number"
                                            defaultValue={item.lifecycle_years}
                                            onChange={(e) => setEditData({...editData, lifecycle_years: parseInt(e.target.value)})}
                                            className="w-16 px-2 py-1 border border-indigo-400 rounded text-sm text-center"
                                          />
                                        ) : item.lifecycle_years || '-'}
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        {isEditing ? (
                                          <div className="flex items-center justify-center gap-1">
                                            <button
                                              onClick={() => handleSaveEdit(item)}
                                              disabled={saving}
                                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                                            >
                                              <Save className="w-4 h-4" />
                                            </button>
                                            <button
                                              onClick={() => { setEditingRow(null); setEditData({}); }}
                                              className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                                            >
                                              <X className="w-4 h-4" />
                                            </button>
                                          </div>
                                        ) : (
                                          <div className="flex items-center justify-center gap-1">
                                            <button
                                              onClick={() => setEditingRow(item.capex_item_id)}
                                              className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                                              title="Edit"
                                            >
                                              <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                              onClick={() => handleDeleteItem(item.capex_item_id)}
                                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                                              title="Delete"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </button>
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add New Row Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Add New CAPEX Row</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SEU Type *</label>
                <select
                  value={newItem.seu_type}
                  onChange={(e) => setNewItem({...newItem, seu_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select SEU Type</option>
                  <option value="A. ELECTRICAL — MOTORS, DRIVES, DISTRIBUTION">A. ELECTRICAL — MOTORS, DRIVES, DISTRIBUTION</option>
                  <option value="B. THERMAL / FUEL">B. THERMAL / FUEL</option>
                  <option value="C. COOLING & HVAC">C. COOLING & HVAC</option>
                  <option value="D. WATER & EFFLUENT">D. WATER & EFFLUENT</option>
                  <option value="E. WASTE">E. WASTE</option>
                  <option value="F. PROCESS & DIGITAL">F. PROCESS & DIGITAL</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opportunity Name *</label>
                <input
                  type="text"
                  value={newItem.opportunity_name}
                  onChange={(e) => setNewItem({...newItem, opportunity_name: e.target.value})}
                  placeholder="e.g., A1. Motors / Drives"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CAPEX Item Name *</label>
                <input
                  type="text"
                  value={newItem.capex_item_name}
                  onChange={(e) => setNewItem({...newItem, capex_item_name: e.target.value})}
                  placeholder="e.g., IE5 Motor"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Technical Specification</label>
                <input
                  type="text"
                  value={newItem.technical_spec}
                  onChange={(e) => setNewItem({...newItem, technical_spec: e.target.value})}
                  placeholder="e.g., IE5, TEFC, S1 duty"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Capacity Basis</label>
                  <input
                    type="text"
                    value={newItem.capacity_basis}
                    onChange={(e) => setNewItem({...newItem, capacity_basis: e.target.value})}
                    placeholder="e.g., kW"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select
                    value={newItem.currency}
                    onChange={(e) => setNewItem({...newItem, currency: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit Cost Low</label>
                  <input
                    type="number"
                    value={newItem.unit_cost_low}
                    onChange={(e) => setNewItem({...newItem, unit_cost_low: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit Cost High</label>
                  <input
                    type="number"
                    value={newItem.unit_cost_high}
                    onChange={(e) => setNewItem({...newItem, unit_cost_high: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Typical Makes/Models (comma-separated)</label>
                <input
                  type="text"
                  value={newItem.typical_makes}
                  onChange={(e) => setNewItem({...newItem, typical_makes: e.target.value})}
                  placeholder="e.g., ABB, Siemens, CG"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lifecycle (years)</label>
                <input
                  type="number"
                  value={newItem.lifecycle_years}
                  onChange={(e) => setNewItem({...newItem, lifecycle_years: parseInt(e.target.value) || 10})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleAddItem}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Adding...' : 'Add Row'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Saving Indicator */}
      {saving && (
        <div className="fixed bottom-4 right-4 bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Saving...
        </div>
      )}
    </div>
  );
};

export default OpportunityDatasetReview;
